import { useQuery } from '@tanstack/react-query';
import { getMe } from './api';
import { todayInZone } from './timezones';

/**
 * The one "today" a screen is allowed to write or judge by.
 *
 * Every date this app stores is UTC and every date it *means* is a day in the account's zone
 * (INV-UTC). The Tasks screens had three answers to "what day is it" and none of them was the
 * account's: `TaskMenu`'s "Plan for today" wrote `new Date().toISOString().slice(0, 10)`, the UTC
 * day, so at 01:00 in Warsaw it planned the task for **yesterday**; "Tomorrow", `CarriedOver`'s
 * "Next week", `GoalCallouts`, `GoalDetail` and `BulkEdit` each assembled the *browser's* day from
 * `getFullYear`/`getMonth`/`getDate`, right only while the laptop sits in the account's zone; and
 * `taskVocab.todayIso()` — now deleted — spread that same browser day across every due pill, the
 * Projects countdown and the `late=` the overdue filter sends to the server.
 *
 * The zone comes back with the day because the two questions are one question. A day string is
 * enough to compare `scheduledDate` against, but turning a stored `dueAt` *instant* into a day
 * needs the zone as well, and that is exactly where `dueLook` was reading the browser's.
 *
 * ## Why a hook and not a prop
 *
 * `Today.tsx` and `Habits.tsx` take `timezone` as a prop from `App.tsx`, which works because they
 * are children of the component holding `me`. `TaskMenu`, `Subtasks` and `CarriedOver` are not —
 * threading it to them means six layers of prop, and the layer that forgets is the layer that
 * quietly goes back to the browser's clock. React Query already caches `['me']` for the whole
 * session, so asking it here is the same fetch `App.tsx` made, not a second one.
 *
 * ## The fallback
 *
 * `App.tsx` renders the workspace only under `signedIn && me.data`, so every caller of this hook
 * is mounted inside a tree where the query has already resolved and `data` is served from cache
 * synchronously. `'UTC'` is what the unreachable branch answers — the same choice `WaitingOn.tsx`
 * makes — rather than `detectTimezone()`: a fixed wrong answer is one somebody can recognise, and
 * falling back to the browser's zone would restore the exact defect this exists to remove, in the
 * one state nobody thinks to look at.
 */
export function useAccountDay(): { timezone: string; today: string } {
  const me = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });
  const timezone = me.data?.timezone ?? 'UTC';
  return { timezone, today: todayInZone(timezone) };
}
